import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { Scale, Plus, Download, Search, CheckCircle, AlertTriangle } from 'lucide-react';

function WeighmentDetails() {
  const { API } = useContext(AppContext);
  const [weighments, setWeighments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchWeighments();
  }, []);

  const fetchWeighments = async () => {
    // Mock data
    setWeighments([
      { id: 'W-001', orderId: 'ORD-2025-001', vehicleNo: 'MH-12-AB-1234', grossWeight: 15500, tareWeight: 5500, netWeight: 10000, expectedWeight: 10000, variance: 0, timestamp: '2025-02-01 08:30', operator: 'Ram Kumar', status: 'Verified' },
      { id: 'W-002', orderId: 'ORD-2025-002', vehicleNo: 'DL-01-CD-5678', grossWeight: 12800, tareWeight: 5300, netWeight: 7500, expectedWeight: 7000, variance: 500, timestamp: '2025-02-01 10:15', operator: 'Shyam Das', status: 'Excess' },
      { id: 'W-003', orderId: 'ORD-2025-003', vehicleNo: 'TN-09-EF-9012', grossWeight: 11200, tareWeight: 5200, netWeight: 6000, expectedWeight: 6500, variance: -500, timestamp: '2025-02-01 14:45', operator: 'Ram Kumar', status: 'Short' },
      { id: 'W-004', orderId: 'ORD-2025-004', vehicleNo: 'GJ-05-GH-3456', grossWeight: 14000, tareWeight: 5000, netWeight: 9000, expectedWeight: 9000, variance: 0, timestamp: '2025-02-02 09:00', operator: 'Vijay Singh', status: 'Verified' },
      { id: 'W-005', orderId: 'ORD-2025-005', vehicleNo: 'KA-01-IJ-7890', grossWeight: 0, tareWeight: 0, netWeight: 0, expectedWeight: 5000, variance: 0, timestamp: '-', operator: '-', status: 'Pending' },
    ]);
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Verified': return 'bg-green-100 text-green-700 border-green-200';
      case 'Excess': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Short': return 'bg-red-100 text-red-700 border-red-200';
      case 'Pending': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const filteredWeighments = weighments.filter(w =>
    w.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.vehicleNo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: weighments.length,
    verified: weighments.filter(w => w.status === 'Verified').length,
    excess: weighments.filter(w => w.status === 'Excess').length,
    short: weighments.filter(w => w.status === 'Short').length,
    pending: weighments.filter(w => w.status === 'Pending').length,
  };

  const totalNet = weighments.reduce((sum, w) => sum + w.netWeight, 0);

  if (loading) {
    return <div className="text-center py-12">Loading weighment data...</div>;
  }

  return (
    <div className="space-y-6" data-testid="weighment-details-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Weighment Details</h1>
          <p className="text-gray-500 mt-1">Track vehicle weighments and variances</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 hover:bg-gray-50">
            <Download size={18} />
            <span>Export</span>
          </button>
          <button className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md">
            <Plus size={18} />
            <span>New Weighment</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Weighments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Scale className="text-gray-400" size={28} />
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center space-x-2">
            <CheckCircle className="text-green-500" size={18} />
            <p className="text-sm text-green-600 font-medium">Verified</p>
          </div>
          <p className="text-2xl font-bold text-green-700">{stats.verified}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
          <p className="text-sm text-blue-600 font-medium">Excess</p>
          <p className="text-2xl font-bold text-blue-700">{stats.excess}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-5 border border-red-200">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="text-red-500" size={18} />
            <p className="text-sm text-red-600 font-medium">Short</p>
          </div>
          <p className="text-2xl font-bold text-red-700">{stats.short}</p>
        </div>
        <div className="bg-gradient-to-br from-[#1a237e]/5 to-[#283593]/10 rounded-xl p-5 border border-[#1a237e]/20">
          <p className="text-sm text-[#1a237e] font-medium">Total Dispatched</p>
          <p className="text-2xl font-bold text-[#1a237e]">{(totalNet / 1000).toFixed(1)} MT</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Search by Order ID or Vehicle..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
        />
      </div>

      {/* Weighment Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Slip No</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Order ID</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Vehicle</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Gross (kg)</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Tare (kg)</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Net (kg)</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Expected</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Variance</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Timestamp</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredWeighments.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 font-mono text-sm text-[#1a237e] font-medium">{item.id}</td>
                  <td className="px-4 py-4 font-mono text-sm text-gray-600">{item.orderId}</td>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{item.vehicleNo}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{item.grossWeight > 0 ? item.grossWeight.toLocaleString() : '-'}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{item.tareWeight > 0 ? item.tareWeight.toLocaleString() : '-'}</td>
                  <td className="px-4 py-4 text-sm font-bold text-gray-900">{item.netWeight > 0 ? item.netWeight.toLocaleString() : '-'}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{item.expectedWeight.toLocaleString()}</td>
                  <td className="px-4 py-4 text-sm">
                    {item.variance !== 0 ? (
                      <span className={`font-medium ${item.variance > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {item.variance > 0 ? '+' : ''}{item.variance.toLocaleString()} kg
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-500">{item.timestamp}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default WeighmentDetails;
